import {
  MessageSquare,
  Plus,
  Upload,
  Settings,
  LogOut,
  Trash2,
  ArrowLeft,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "./ui/sidebar";
import { Button } from "./ui/button";
import api from "../lib/api.js";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import { ScrollArea } from "./ui/scroll-area";
import { useNavigate } from "react-router-dom";

export function AppSidebar({
  activeSessionId,
  onNewChat,
  onSelectSession,
  onUploadResume,
}) {
  const navigate = useNavigate();
  const { state } = useSidebar();
  const [sessions, setSessions] = useState([]);
  const collapsed = state === "collapsed";

  useEffect(() => {
    loadSessions();
  }, [activeSessionId]);

  const loadSessions = async () => {
    try {
      const data = await api.getSessions();
      console.log("Loaded sessions:", data.data.sessions);
      setSessions(data.data.sessions || []);
    } catch (error) {
      console.error("Error loading sessions:", error);
    }
  };

  const handleNewChat = async () => {
    await onNewChat();
    await loadSessions();
  };

  const handleDeleteSession = async (sessionId, e) => {
    e.stopPropagation();

    if (!confirm("Are you sure you want to delete this conversation?")) {
      return;
    }

    try {
      await api.deleteSession(sessionId);
      toast.success("Conversation deleted");
      await loadSessions();

      if (activeSessionId === sessionId) {
        await onNewChat();
      }
    } catch (error) {
      console.error("Error deleting session:", error);
      toast.error("Failed to delete conversation");
    }
  };

  const handleBackToApp = () => {
    navigate("/user/dashboard");
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return "Today";
    } else if (diffInHours < 48) {
      return "Yesterday";
    } else if (diffInHours < 168) {
      return `${Math.floor(diffInHours / 24)} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  console.log("Sessions:", sessions);
  console.log("activeSessionId", activeSessionId);

  return (
    <Sidebar className={collapsed ? "w-16" : "w-64 md:w-72"} collapsible="icon">
      <SidebarHeader className="border-b border-sidebar-border p-3 md:p-4">
        {!collapsed && (
          <div className="flex items-center justify-between">
            <h2 className="text-base md:text-lg font-bold text-sidebar-foreground truncate">
              Moil Career Coach
            </h2>
          </div>
        )}
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <Button
                  onClick={handleNewChat}
                  className="w-full justify-start gap-2 text-sm md:text-base h-9 md:h-10"
                  variant="default"
                >
                  <Plus className="h-4 w-4 flex-shrink-0" />
                  {!collapsed && <span>New Chat</span>}
                </Button>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <Button
                  onClick={onUploadResume}
                  className="w-full justify-start gap-2 text-sm md:text-base h-9 md:h-10"
                  variant="outline"
                >
                  <Upload className="h-4 w-4 flex-shrink-0" />
                  {!collapsed && <span>Upload Resume</span>}
                </Button>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {!collapsed && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-xs md:text-sm">Recent Conversations</SidebarGroupLabel>
            <SidebarGroupContent>
              <ScrollArea className="h-[300px] md:h-[400px]">
                <SidebarMenu>
                  {sessions?.map((session) => (
                    <SidebarMenuItem key={session.id}>
                      <div className="flex items-center gap-1 group">
                        <SidebarMenuButton
                          onClick={() => onSelectSession(session.id)}
                          isActive={activeSessionId === session.id}
                          className="flex-1 justify-start gap-2 min-h-[44px] md:min-h-[48px]"
                        >
                          <MessageSquare className="h-4 w-4 flex-shrink-0" />
                          <div className="flex flex-col items-start flex-1 min-w-0">
                            <span className="truncate text-xs md:text-sm w-full">
                              {session.title ||
                                (session.session_type === "general"
                                  ? "Career Chat"
                                  : session.session_type)}
                            </span>
                            <span className="text-[10px] md:text-xs text-muted-foreground">
                              {formatDate(session.created_at)}
                            </span>
                          </div>
                        </SidebarMenuButton>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 md:h-9 md:w-9 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                          onClick={(e) => handleDeleteSession(session.id, e)}
                        >
                          <Trash2 className="h-3 w-3 md:h-4 md:w-4 text-destructive" />
                        </Button>
                      </div>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </ScrollArea>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-3 md:p-4">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={handleBackToApp}
              className="w-full justify-start gap-2 text-sm md:text-base min-h-[44px] md:min-h-[48px]"
            >
              <ArrowLeft className="h-4 w-4 flex-shrink-0" />
              {!collapsed && <span>Back to App</span>}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
